import React, { useEffect, useState } from "react";
import CompSearchField from "components/CompSearchField";
import CompDropdown from "components/CompDropdown";
import Title from "components/Title";
import * as S from "./styles";
import { useApi } from "hooks/useApis";
import { z } from "zod";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgress, Grid } from "@mui/material";
import {
  status_options,
  type_options,
  distributionPoints,
} from "utils/constants";
import Typography from "@mui/material/Typography";
import CompAutocomplete from "components/CompAutocomplete";
import { lightAmarelo } from "styles/variables";
import { DownloadPayrollButton } from "components/DownloadPayrollButton";
import TablePayrollBenefits from "components/TablePayrollBenefits";
import { useParams } from "react-router-dom";

import AgentCity from "utils/agentCity";
import AgentType from "utils/agentType";

const StatusCard = ({ title, number, description, percentageChange }) => {
  const percentageColor = percentageChange >= 0 ? "#27AE60" : "#EB5757";

  return (
    <S.StatusCard>
      <h3 className="status-number">{number}</h3>
      <p className="status-title">{title}</p>
      <span className="status-description">{description}</span>
    </S.StatusCard>
  );
};

const BeneficiaryPayroll = () => {
  const api = useApi();

  const [beneficiaryList, setBeneficiaryList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [payrollDate, setPayrollDate] = useState("");
  const [payrollId, setPayrollId] = useState(null);

  const [totalValueActive, setTotalValueActive] = useState(null);
  const [totalValueWarning, setTotalValueWarning] = useState(null);
  const [totalValueBlocked, setTotalValueBlocked] = useState(null);
  const [totalPaid, setTotalPaid] = useState(null);
  const [totalValuePerCity, setTotalValuePerCity] = useState(null);


  const agent_type = AgentType();
  const agent_city = AgentCity();

  const [sheetUrl, setSheetUrl] = useState("");

   // Captura o estado de todos os filtros usando o useForm
   const createBeneficiarySearchSchema = z
   .object({
     b_status: z.string().optional(),
     b_type: z.string().optional(),
     search: z.string().optional(),
     city: z.string().optional(),
   });

 const methods = useForm({
   resolver: zodResolver(createBeneficiarySearchSchema),
   mode: "all",
   criteriaMode: "all",
 });

  // Monitoramento dos valores dos filtros
  const b_status_value = methods.watch("b_status");
  const b_type_value = methods.watch("b_type");
  const search_value = methods.watch("search");
  const city_value = methods.watch("city");

  const { id } = useParams();
  useEffect(() => {
    if (id) {
      setPayrollId(id); // Salva o ID no estado
    }
  }, [id]);


  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        const data = await api.getPayrollById(payrollId);
        
        setSheetUrl(data.sheet);

        const city = city_value; 
      
        if (agent_type === "1" || agent_type === "4" || agent_type === "6") {

          // Soma os valores de todas as cidades
          const total_value_active = data.total_value_per_status_1[city] || data.total_value_per_status_1.total || 0;
          const total_value_warning = data.total_value_per_status_2[city] || data.total_value_per_status_2.total || 0;
          const total_value_blocked = data.total_value_per_status_3[city] || data.total_value_per_status_3.total || 0;
          const total_paid_value = data.total_paid_value_per_city[city] || data.total_paid_value_per_city.total || 0;
          const total_value = data.total_value_per_city[city] || data.total_value || 0;

          setTotalValueActive(total_value_active);
          setTotalValueWarning(total_value_warning);
          setTotalValueBlocked(total_value_blocked);
          setTotalPaid(total_paid_value);
          setTotalValuePerCity(total_value);
        } else {
          // Obtém os valores para a cidade específica
          const total_value_active = data.total_value_per_status_1[city] || 0;
          const total_value_warning = data.total_value_per_status_2[city]|| 0;
          const total_value_blocked = data.total_value_per_status_3[city]|| 0;
          const total_paid_value = data.total_paid_value_per_city[city]|| 0;
          const total_value_per_city = data.total_value_per_city[city]|| 0;

         
            setTotalValueActive(total_value_active);
            setTotalValueWarning(total_value_warning);
            setTotalValueBlocked(total_value_blocked);
            setTotalPaid(total_paid_value);
            setTotalValuePerCity(total_value_per_city);
          } 
        
      } catch (error) {
        console.error("Error fetching payroll data:", error);
      }
    };
   
    if (payrollId) {
      fetchPayrollData(); // Chama a função para atualizar os valores dos cards
    }    
  }, [payrollId, city_value, agent_type, agent_city]);


  useEffect(() => {
    methods.handleSubmit(onSubmit, onError)();
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === 1) {
      methods.handleSubmit(onSubmit, onError)();
    } else {
      setCurrentPage(1);
    }
  }, [b_status_value, b_type_value, city_value, search_value]);

  async function onSubmit(data) {
    try {
      setLoading(true);

      const filteredList = await api.listPayrollBenefits(
        currentPage,
        data.search,
        data.b_status,
        data.b_type,
        payrollId,
        data.city,
      );
      setBeneficiaryList(filteredList.results);
      setTotalPages(filteredList.total_pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function onError(errors) {
    console.log("Failed to submit:", errors);
  }


  return (
    <S.PageContainer>
      <Title
        titleText={`Lista de beneficiários da folha ${payrollDate}`}
        subtitleTexts={["Início", `Lista de beneficiários da folha ${payrollDate}`]}
      />
      <S.PageContent>
        <S.MainContainer>
          <FormProvider {...methods}>
            <S.Form onSubmit={methods.handleSubmit(onSubmit, onError)}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <S.DateFieldsTitle>
                    BUSCAR POR BENEFICIÁRIO
                  </S.DateFieldsTitle>
                  <CompSearchField
                    placeholder="Digite o nome ou NIS ou CPF para buscar"
                    name="search"
                    register={methods.register}
                    unregister={methods.unregister}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <CompDropdown
                    style={{ flexGrow: "1" }}
                    defaultValue=""
                    textLabel="TIPO DE BENEFICIÁRIO"
                    options={type_options}
                    name="b_type"
                    register={methods.register}
                    unregister={methods.unregister}
                  />
                </Grid>

              </Grid>
              <Grid container spacing={2} alignItems="center" marginTop={2}>
                <Grid item xs={12} sm={5}>
                  <CompDropdown
                    style={{ flexGrow: "1" }}
                    defaultValue=""
                    textLabel="STATUS DO BENEFÍCIARIO"
                    options={status_options.filter(
                      (option) => option.value !== "1"
                    )}
                    name="b_status"
                    register={methods.register}
                    unregister={methods.unregister}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <CompAutocomplete
                    style={{ flexGrow: "1" }}
                    defaultValue={["2"].includes(agent_type) ? agent_city : city_value}
                    textLabel="MUNICÍPIO:"
                    options={distributionPoints}
                    name="city"
                    placeholder="Selecione uma município"
                    register={methods.register}
                    unregister={methods.unregister}
                  />
                </Grid>
              </Grid>


              <S.CabecalhoTabela>
                <Typography
                  variant="h6"
                  style={{ marginTop: 0, marginBottom: 0 }}
                >
                  Dados de pagamento
                </Typography>
                <S.FormHeader>
                  <StatusCard
                    title="Total de Beneficiários pagos"
                    number={totalValuePerCity}
                  // percentageChange={0}
                  />
                  <StatusCard
                    title="Valor total pago"
                    number={"R$" + totalPaid}
                  // percentageChange={0}
                  />
                </S.FormHeader>
                <Typography
                  variant="h6"
                  style={{ marginTop: 15, marginBottom: 0 }}
                >
                  Total de beneficiários pagos por status
                </Typography>
                <S.FormHeader>
                  <StatusCard
                    title="Beneficiários ativos"
                    number={totalValueActive}
                  />
                  <StatusCard
                    title="Beneficiários advertidos"
                    number={totalValueWarning}
                  />
                  <StatusCard
                    title="Beneficiários bloqueados"
                    number={totalValueBlocked}
                  />
                </S.FormHeader>
              </S.CabecalhoTabela>
            </S.Form>
          </FormProvider>

          {loading ? (
            <div
              style={{
                width: "100%",
                height: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress style={{ color: lightAmarelo }} />
            </div>
          ) : (
            <TablePayrollBenefits
              beneficiaryList={beneficiaryList}
              page={currentPage}
              totalPages={totalPages}
              setPage={setCurrentPage}
            />
          )}
          {!["2","3", "6", "5"].includes(agent_type) && (
            <DownloadPayrollButton payrollId={payrollId} />
          )}

        </S.MainContainer>
      </S.PageContent>
    </S.PageContainer>
  );
};

export default BeneficiaryPayroll;
